// «Promos de tus accesorios»: un bloque por accesorio ya en la venta, con «Va sola» y «Se suman».
// Sin preselección; el descuento de solo accesorios no sale aquí: lo aparta `AccessoryDiscountSync`.
import { Package } from 'lucide-react';
import { NoneChoiceRow, PromotionGroup } from '@/domains/sales/wizard/components/PromotionGroup';
import type { AnchorSlotContext } from '@/domains/sales/wizard/steps/BuildSaleStep';
import { useSalePromotionEvaluation, useStoreSalePromotions } from '../api/useWizardPromotions';
import type { EvaluatedSalePromotion } from '../api/wizardPromotionsContract';
import { useAnchorSaleContext } from '../hooks/useAnchorSaleContext';
import { useApplyPromotions } from '../hooks/useApplyPromotions';
import { accessoryPromotionsFor, type AccessoryPromotions } from '../lib/accessoryPromotions';
import { onlyLowersAccessories } from '../lib/phoneDraft';
import type { SelectionChange } from '../lib/applySelection';
import {
    EMPTY_LINE,
    normalizeKinds,
    withSolo,
    withStackable,
    type LineSelection,
    type PromotionChoice,
    type PromotionSelection,
} from '../lib/promotionSelection';
import { splitByBenefit } from '../lib/skuPromotionPieces';
import { usePreviewSelection } from '../store/promotionSelectionStore';
import { afterSaleText } from './AfterSaleBenefits';
import { GROUPS, SectionTitle, SoloPromotionRow, StackableChoice } from './PromotionChoices';

const choiceOf = (promo: EvaluatedSalePromotion): PromotionChoice => ({
    promotionId: promo.promotionId,
    promotionVersion: promo.version,
    giftSkus: promo.giftOptions
        .filter((o) => o.inStock)
        .slice(0, 1)
        .map((o) => o.sku),
});

/** Bloque de un accesorio: sus promos en las cajas de color del teléfono. */
function AccessoryBlock({
    title,
    accessory,
    line,
    busy,
    names,
    onSolo,
    onToggle,
}: {
    title: string;
    accessory: AccessoryPromotions;
    line: LineSelection;
    busy: boolean;
    names: ReadonlyMap<string, string>;
    onSolo: (promo: EvaluatedSalePromotion | null) => void;
    onToggle: (promo: EvaluatedSalePromotion, on: boolean) => void;
}) {
    const byId = new Map(accessory.promos.map((p) => [p.promotionId, p]));
    const solos = splitByBenefit(
        accessory.promos.filter((p) => !p.stackable),
        afterSaleText,
    );
    const stackables = splitByBenefit(
        accessory.promos.filter((p) => p.stackable),
        afterSaleText,
    );
    const hasSolos = GROUPS.some(({ group }) => solos[group].length > 0);
    const hasStackables = GROUPS.some(({ group }) => stackables[group].length > 0);
    return (
        <section
            aria-label={title}
            className="flex flex-col gap-1 rounded-lg border border-border bg-card p-2.5"
        >
            <div className="truncate text-[13px] font-semibold" title={title}>
                {title}
            </div>
            {hasSolos && (
                <>
                    <SectionTitle title="Va sola" hint="elige una" />
                    <div
                        role="radiogroup"
                        aria-label={`Va sola · ${title}`}
                        className="flex flex-col"
                    >
                        {GROUPS.map(({ group, label, icon }) => (
                            <PromotionGroup
                                key={group}
                                label={label}
                                icon={icon}
                                tone={group}
                                limit={Number.POSITIVE_INFINITY}
                                items={solos[group].flatMap((piece) => {
                                    const promo = byId.get(piece.promotionId);
                                    if (!promo) return [];
                                    return [
                                        {
                                            key: piece.promotionId,
                                            node: (
                                                <SoloPromotionRow
                                                    piece={piece}
                                                    promo={promo}
                                                    icon={icon}
                                                    checked={
                                                        line.solo?.promotionId === promo.promotionId
                                                    }
                                                    disabled={Boolean(promo.blockedBy) || busy}
                                                    names={names}
                                                    onSelect={() => {
                                                        onSolo(promo);
                                                    }}
                                                />
                                            ),
                                        },
                                    ];
                                })}
                            />
                        ))}
                        <div className="mt-3">
                            <NoneChoiceRow
                                label="Ninguna"
                                checked={line.solo === null}
                                disabled={busy}
                                onSelect={() => {
                                    onSolo(null);
                                }}
                            />
                        </div>
                    </div>
                </>
            )}
            {hasStackables && (
                <>
                    <SectionTitle title="Se suman" hint="marca las que quieras" />
                    <div role="group" aria-label={`Se suman · ${title}`} className="flex flex-col">
                        {GROUPS.map(({ group, label, icon }) => (
                            <PromotionGroup
                                key={group}
                                label={label}
                                icon={icon}
                                tone={group}
                                limit={Number.POSITIVE_INFINITY}
                                items={stackables[group].flatMap((piece) => {
                                    const promo = byId.get(piece.promotionId);
                                    if (!promo) return [];
                                    const checked = line.stackables.some(
                                        (c) => c.promotionId === promo.promotionId,
                                    );
                                    return [
                                        {
                                            key: piece.promotionId,
                                            node: (
                                                <StackableChoice
                                                    piece={piece}
                                                    icon={icon}
                                                    checked={checked}
                                                    disabled={Boolean(promo.blockedBy) || busy}
                                                    description={[...piece.texts, piece.extras]
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                    onToggle={() => {
                                                        onToggle(promo, !checked);
                                                    }}
                                                />
                                            ),
                                        },
                                    ];
                                })}
                            />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}

export function AccessoryPromotionsSection({
    sessionId,
    context,
}: {
    sessionId: string;
    context: AnchorSlotContext;
}) {
    const { data } = useSalePromotionEvaluation(sessionId);
    const { data: store } = useStoreSalePromotions(sessionId);
    const selection = usePreviewSelection(sessionId);
    const { apply, error } = useApplyPromotions({ sessionId, context });
    const { withoutSureKeep, catalogInfo, lineNames } = useAnchorSaleContext(
        sessionId,
        context.anchorLine,
    );
    const lines = context.extraLines ?? [];
    if (!data || lines.length === 0) return null;

    const storeItems = store?.items ?? [];
    const accessories = [...new Set(lines.map((l) => l.sku))]
        .sort()
        .map((sku) =>
            accessoryPromotionsFor(sku, lines, data, storeItems, catalogInfo(sku), withoutSureKeep),
        )
        .map((a) => ({ ...a, promos: a.promos.filter((p) => !onlyLowersAccessories(p)) }))
        .filter((a) => a.promos.length > 0);
    if (accessories.length === 0) return null;

    const view = normalizeKinds(selection, data);
    const busy = context.quoteState === 'charged';
    const run = (change: SelectionChange): void => {
        context
            .confirmQuoteDiscard()
            .then((ok) => (ok ? apply(change) : undefined))
            .catch(() => undefined);
    };
    /** El cambio en todas las piezas del accesorio, sobre la selección ya acomodada. */
    const onLines =
        (
            lineIds: readonly string[],
            each: (s: PromotionSelection, id: string) => PromotionSelection,
        ) =>
        (base: PromotionSelection) =>
            lineIds.reduce(each, normalizeKinds(base, data));

    return (
        <section
            aria-label="Promos de tus accesorios"
            className="mb-3 flex flex-col gap-3 rounded-xl border border-dashed border-border bg-muted/30 p-3"
        >
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                <Package className="size-3.5" aria-hidden />
                Promos de tus accesorios
            </div>
            {accessories.map((accessory) => {
                const firstLine = accessory.lineIds[0];
                const line = (firstLine ? view.byLine[firstLine] : undefined) ?? EMPTY_LINE;
                const name =
                    catalogInfo(accessory.sku)?.name ??
                    (firstLine ? lineNames.get(firstLine) : undefined) ??
                    accessory.sku;
                const title = accessory.qty > 1 ? `${name} × ${String(accessory.qty)}` : name;
                return (
                    <AccessoryBlock
                        key={accessory.sku}
                        title={title}
                        accessory={accessory}
                        line={line}
                        busy={busy}
                        names={lineNames}
                        onSolo={(promo) => {
                            run(
                                onLines(accessory.lineIds, (s, id) =>
                                    withSolo(s, id, promo ? choiceOf(promo) : null),
                                ),
                            );
                        }}
                        onToggle={(promo, on) => {
                            run(
                                onLines(accessory.lineIds, (s, id) =>
                                    withStackable(s, id, choiceOf(promo), on),
                                ),
                            );
                        }}
                    />
                );
            })}
            {error && (
                <p role="alert" className="text-[12px] text-destructive">
                    {error}
                </p>
            )}
        </section>
    );
}
